import { useCallback, useEffect, useState, type FormEvent, type ReactElement } from 'react';
import { DOI_DECKS_API_URL } from '../../constants';
import { activityFromAudit } from '../../domain/activityLog';
import { ActivityTable } from '../activity/ActivityTable';

type AuthFetch = (url: string, init?: RequestInit) => Promise<Response>;

interface LiveDoiPanelProps {
  authFetch: AuthFetch;
  contractId: string;
  asOf: string;
  canWrite: boolean;
  compact?: boolean;
}

interface DoiLine {
  id: string;
  partyId: string;
  partyName: string;
  interestClass: string;
  decimal: string;
  effectiveFrom: string;
  effectiveTo?: string;
  payStatus: string;
}

interface DoiDeck {
  id: string;
  kind: string;
  status: string;
  effectiveFrom: string;
  effectiveTo?: string;
  lines: DoiLine[];
  sourceContractId?: string;
  sourceContractNumber?: string;
}

interface AuditRow {
  id: string;
  actor: string;
  occurredAtUtc: string;
  action: string;
  beforeJson?: string;
  afterJson?: string;
  description?: string;
}

interface DoiResult {
  decks: DoiDeck[];
  asOf: {
    asOf: string;
    workingInterest: DoiLine[];
    revenue: DoiLine[];
    workingInterestSum: string;
    revenueSum: string;
    nriPreview?: string;
  };
  audit: AuditRow[];
  parties: PartyOpt[];
}

interface PartyOpt {
  id: string;
  legalName: string;
}

interface DraftLine {
  partyId: string;
  interestClass: string;
  decimal: string;
  payStatus: string;
}

async function readJson<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Request failed (${res.status})`);
  }
  return (await res.json()) as T;
}

function emptyLine(interestClass: string): DraftLine {
  return { partyId: '', interestClass, decimal: '0.00000000', payStatus: 'Pay' };
}

export function LiveDoiPanel(props: LiveDoiPanelProps): ReactElement {
  const { authFetch, contractId, asOf, canWrite, compact } = props;
  const [data, setData] = useState<DoiResult | null>(null);
  const [error, setError] = useState('');
  const [wiLines, setWiLines] = useState<DraftLine[]>([
    emptyLine('WorkingInterest'),
    emptyLine('WorkingInterest')
  ]);
  const [revLines, setRevLines] = useState<DraftLine[]>([
    emptyLine('NetRevenueInterest'),
    emptyLine('Royalty'),
    emptyLine('OverridingRoyalty')
  ]);

  const load = useCallback(async () => {
    if (!contractId || !asOf) {
      return;
    }
    const result = await readJson<DoiResult>(
      await authFetch(`${DOI_DECKS_API_URL}?contractId=${encodeURIComponent(contractId)}&asOf=${encodeURIComponent(asOf)}`)
    );
    setData(result);
    const citation = result.parties.find((p) => p.legalName.includes('Citation Oil'));
    const partner = result.parties.find((p) => p.legalName === 'Partner Co');
    const owner = result.parties.find((p) => p.legalName.includes('Demo Mineral Owner'));
    setWiLines((rows) =>
      rows[0]?.partyId
        ? rows
        : [
            { partyId: citation?.id ?? '', interestClass: 'WorkingInterest', decimal: '0.80000000', payStatus: 'Pay' },
            { partyId: partner?.id ?? '', interestClass: 'WorkingInterest', decimal: '0.20000000', payStatus: 'Pay' }
          ]
    );
    setRevLines((rows) =>
      rows[0]?.partyId
        ? rows
        : [
            { partyId: citation?.id ?? '', interestClass: 'NetRevenueInterest', decimal: '0.80000000', payStatus: 'Pay' },
            { partyId: owner?.id ?? '', interestClass: 'Royalty', decimal: '0.20000000', payStatus: 'Pay' },
            { partyId: partner?.id ?? '', interestClass: 'OverridingRoyalty', decimal: '0.00000000', payStatus: 'Pay' }
          ]
    );
  }, [asOf, authFetch, contractId]);

  useEffect(() => {
    if (!contractId) {
      return;
    }
    void load().catch((e) => setError(e instanceof Error ? e.message : 'Could not load DOI'));
  }, [contractId, load]);

  async function saveAndMaybePost(kind: 'WorkingInterest' | 'Revenue', lines: DraftLine[], post: boolean): Promise<void> {
    setError('');
    try {
      const created = await readJson<DoiDeck>(
        await authFetch(DOI_DECKS_API_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            contractId,
            kind,
            effectiveFrom: asOf,
            lines: lines
              .filter((l) => l.partyId && Number(l.decimal) > 0)
              .map((l) => ({
                partyId: l.partyId,
                interestClass: l.interestClass,
                decimal: l.decimal,
                effectiveFrom: asOf,
                payStatus: l.payStatus
              }))
          })
        })
      );
      if (post) {
        await readJson<DoiDeck>(
          await authFetch(`${DOI_DECKS_API_URL}/${created.id}/post`, { method: 'POST' })
        );
      }
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message.replace(/^"|"$/g, '') : 'DOI save failed');
    }
  }

  function onWi(e: FormEvent, post: boolean): void {
    e.preventDefault();
    void saveAndMaybePost('WorkingInterest', wiLines, post);
  }

  function onRev(e: FormEvent, post: boolean): void {
    e.preventDefault();
    void saveAndMaybePost('Revenue', revLines, post);
  }

  return (
    <section className={compact ? 'nested-card' : 'card'} data-testid="live-doi" aria-label="Live DOI">
      <h4 className="form-section-title">Live DOI as-of {asOf || '—'}</h4>
      {error && (
        <p className="error" data-testid="doi-error">
          {error}
        </p>
      )}
      {data && (
        <>
          <p data-testid="doi-asof-header">
            WI {data.asOf.workingInterestSum} · Revenue {data.asOf.revenueSum}
            {data.asOf.nriPreview ? ` · NRI preview ${data.asOf.nriPreview}` : ''}
          </p>
          <LineTable title="Working interest in force" lines={data.asOf.workingInterest} testId="doi-wi-asof" />
          <LineTable title="Revenue classes in force" lines={data.asOf.revenue} testId="doi-rev-asof" />
          {data.decks.some((d) => d.sourceContractNumber) && (
            <ul className="collaboration-notes-list" data-testid="doi-sources">
              {data.decks
                .filter((d) => d.sourceContractNumber)
                .map((d) => (
                  <li key={d.id} className="collaboration-note-card">
                    <div className="collaboration-note-body">
                      {d.kind} {d.effectiveFrom} from {d.sourceContractNumber}
                    </div>
                  </li>
                ))}
            </ul>
          )}
        </>
      )}
      {canWrite && (
        <>
          <form className="grid" data-testid="doi-wi-form" onSubmit={(e) => onWi(e, true)}>
            <h4 className="form-section-title">Post WI deck</h4>
            {wiLines.map((line, i) => (
              <DraftRow
                key={`wi-${i}`}
                line={line}
                parties={data?.parties ?? []}
                classes={['WorkingInterest']}
                onChange={(next) => setWiLines((rows) => rows.map((r, idx) => (idx === i ? next : r)))}
              />
            ))}
            <div className="row">
              <button type="button" className="secondary" onClick={(e) => onWi(e, false)}>
                Save draft
              </button>
              <button type="submit" data-testid="doi-post-wi">
                Post WI
              </button>
            </div>
          </form>
          <form className="grid" data-testid="doi-rev-form" onSubmit={(e) => onRev(e, true)}>
            <h4 className="form-section-title">Post revenue deck</h4>
            {revLines.map((line, i) => (
              <DraftRow
                key={`rev-${i}`}
                line={line}
                parties={data?.parties ?? []}
                classes={['NetRevenueInterest', 'Royalty', 'OverridingRoyalty']}
                onChange={(next) => setRevLines((rows) => rows.map((r, idx) => (idx === i ? next : r)))}
              />
            ))}
            <div className="row">
              <button type="button" className="secondary" onClick={(e) => onRev(e, false)}>
                Save draft
              </button>
              <button type="submit" data-testid="doi-post-rev">
                Post revenue
              </button>
            </div>
          </form>
        </>
      )}
      {data && (
        <ActivityTable
          testId="doi-audit"
          rows={data.audit.slice(0, compact ? 4 : 8).map((row) => activityFromAudit(row))}
        />
      )}
      {!canWrite && <p className="hint">Read-only. Division Order posts live decks.</p>}
    </section>
  );
}

function LineTable({
  title,
  lines,
  testId
}: {
  title: string;
  lines: DoiLine[];
  testId: string;
}): ReactElement {
  return (
    <div className="requests-grid-wrap">
      <table className="requests-grid" data-testid={testId}>
        <thead>
          <tr>
            <th>{title}</th>
            <th>Class</th>
            <th>Decimal</th>
            <th>Pay</th>
          </tr>
        </thead>
        <tbody>
          {lines.length === 0 ? (
            <tr>
              <td colSpan={4}>None in force</td>
            </tr>
          ) : (
            lines.map((l) => (
              <tr key={l.id}>
                <td>{l.partyName}</td>
                <td>{l.interestClass}</td>
                <td className="mono-id">{l.decimal}</td>
                <td>{l.payStatus}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

function DraftRow({
  line,
  parties,
  classes,
  onChange
}: {
  line: DraftLine;
  parties: PartyOpt[];
  classes: string[];
  onChange: (line: DraftLine) => void;
}): ReactElement {
  return (
    <div className="row">
      <select value={line.partyId} onChange={(e) => onChange({ ...line, partyId: e.target.value })}>
        <option value="">Party</option>
        {parties.map((p) => (
          <option key={p.id} value={p.id}>
            {p.legalName}
          </option>
        ))}
      </select>
      <select value={line.interestClass} onChange={(e) => onChange({ ...line, interestClass: e.target.value })}>
        {classes.map((c) => (
          <option key={c} value={c}>
            {c}
          </option>
        ))}
      </select>
      <input value={line.decimal} onChange={(e) => onChange({ ...line, decimal: e.target.value })} />
      <select value={line.payStatus} onChange={(e) => onChange({ ...line, payStatus: e.target.value })}>
        <option>Pay</option>
        <option>Suspend</option>
      </select>
    </div>
  );
}
